import { Link } from 'react-router-dom';
import { Card } from '@/components/Card/Card';
import { Avatar } from '@/components/Avatar/Avatar';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { t } from '@/lib/i18n';
import { formatRelativeTime } from '@/lib/format';
import type { JobListItem, JobStatus } from '@/features/jobs/schemas';
import styles from './ActivityFeed.module.css';

const MAX_VISIBLE = 5;

type PillVariant = 'info' | 'success' | 'warning' | 'danger' | 'muted';

export function statusPillVariant(status: JobStatus): PillVariant {
  switch (status) {
    case 'running':
      return 'info';
    case 'awaiting_input':
      return 'warning';
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'muted';
  }
}

export function statusPillLabel(status: JobStatus): string {
  switch (status) {
    case 'running':
      return t('Working');
    case 'awaiting_input':
      return t('Needs input');
    case 'completed':
      return t('Done');
    case 'failed':
      return t('Blocked');
    case 'cancelled':
      return t('Cancelled');
  }
}

export function synthesizeSummary(job: JobListItem): string {
  const name = job.assigned_member.display_name;
  switch (job.status) {
    case 'running':
      return t('{name} is working on {title}.', { name, title: job.title });
    case 'awaiting_input':
      return t('{name} needs your input on {title}.', { name, title: job.title });
    case 'completed':
      return t('{name} completed {title}.', { name, title: job.title });
    case 'failed':
      return t('{name} got stuck on {title}.', { name, title: job.title });
    case 'cancelled':
      return t('{title} was cancelled.', { title: job.title });
  }
}

export interface ActivityItem {
  id: string;
  title: string;
  memberName: string;
  memberKind: 'ai' | 'human';
  pillVariant: PillVariant;
  pillLabel: string;
  summary: string;
  timestamp: string;
}

export function jobToActivityItem(
  job: JobListItem,
  kindLookup?: ReadonlyMap<string, 'ai' | 'human'>,
): ActivityItem {
  const kind = kindLookup?.get(job.assigned_member.id) ?? 'ai';
  return {
    id: job.id,
    title: job.title,
    memberName: job.assigned_member.display_name,
    memberKind: kind,
    pillVariant: statusPillVariant(job.status),
    pillLabel: statusPillLabel(job.status),
    summary: synthesizeSummary(job),
    timestamp: job.created_at,
  };
}

export function pickRecentJobs(jobs: JobListItem[]): JobListItem[] {
  return [...jobs]
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .slice(0, MAX_VISIBLE);
}

export interface ActivityFeedProps {
  jobs: JobListItem[];
  kindByMemberId?: ReadonlyMap<string, 'ai' | 'human'>;
}

export function ActivityFeed({ jobs, kindByMemberId }: ActivityFeedProps) {
  const items = pickRecentJobs(jobs).map((j) => jobToActivityItem(j, kindByMemberId));

  return (
    <Card padding="md">
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('Activity')}</h2>
          <div className={styles.actions}>
            <Link to="/jobs/new" className={styles.actionLink}>
              {t('+ New job')}
            </Link>
            <Link to="/jobs" className={styles.actionLink}>
              {t('All activity →')}
            </Link>
          </div>
        </div>
        {items.length === 0 ? (
          <p className={styles.emptyLine}>{t('No activity yet.')}</p>
        ) : (
          <ul className={styles.list}>
            {items.map((item) => (
              <li key={item.id} className={styles.row} data-testid="activity-item">
                <div className={styles.rowHeader}>
                  <Avatar
                    name={item.memberName}
                    size="sm"
                    kind={item.memberKind}
                    aria-label={item.memberName}
                  />
                  <span className={styles.memberName}>{item.memberName}</span>
                  <StatusPill variant={item.pillVariant}>{item.pillLabel}</StatusPill>
                  <span className={styles.timestamp}>{formatRelativeTime(item.timestamp)}</span>
                </div>
                <Link to={`/jobs/${encodeURIComponent(item.id)}`} className={styles.titleLink}>
                  {item.title}
                </Link>
                <p className={styles.summary}>{item.summary}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Card>
  );
}
